// src/pages/Coleta/components/VerificacoesPagina.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Chip, Link, Paper, Stack, Typography } from '@mui/material';
import * as Lucide from 'lucide-react';
import { EVENTO_PLANOS_ALTERADOS, listarPlanosAcao } from '../../../infra/api/PlanosAcao';
import { AZUL_CLARO } from '../../../theme/cores';

const MAXIMO = 5;

/**
 * "Verificações relacionadas à água": as ações do plano de ações desta página (registros
 * de limpeza, novas coletas), as abertas primeiro. O detalhe e a conclusão ficam em /acoes.
 */
export default function VerificacoesPagina({ pagina, titulo }) {
    const navigate = useNavigate();
    const { tenant } = useParams();
    const [itens, setItens] = useState(null);

    useEffect(() => {
        let vivo = true;
        const carregar = () =>
            Promise.all([
                listarPlanosAcao({ pagina, status: 'ABERTA', size: MAXIMO }),
                listarPlanosAcao({ pagina, status: 'CONCLUIDA', size: MAXIMO }),
            ])
                .then(([abertas, concluidas]) => vivo && setItens([...abertas.items, ...concluidas.items].slice(0, MAXIMO)))
                .catch(() => vivo && setItens([]));
        carregar();
        window.addEventListener(EVENTO_PLANOS_ALTERADOS, carregar);
        return () => {
            vivo = false;
            window.removeEventListener(EVENTO_PLANOS_ALTERADOS, carregar);
        };
    }, [pagina]);

    return (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 1.5 }}>
                <Typography fontWeight={700} sx={{ fontSize: '1.1rem' }}>{titulo}</Typography>
                <Typography variant="body2" color="text.secondary">Acompanhe os registros de limpeza e as novas coletas.</Typography>
            </Box>
            {itens === null ? null : itens.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
                    Nenhuma verificação registrada para esta página.
                </Typography>
            ) : (
                <Stack sx={{ px: { xs: 2, sm: 3 } }}>
                    {itens.map(({ plano, vencida }) => {
                        const concluida = plano.status === 'CONCLUIDA';
                        return (
                            <Stack
                                key={plano.id}
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{ py: 1.75, borderTop: 1, borderColor: 'divider' }}
                            >
                                <Lucide.ClipboardList size={18} />
                                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                    <Typography variant="body2" fontWeight={700}>{plano.titulo}</Typography>
                                    {plano.responsavel && (
                                        <Typography variant="caption" color="text.secondary">{plano.responsavel}</Typography>
                                    )}
                                </Box>
                                <Chip
                                    size="small"
                                    label={concluida ? 'Concluída' : vencida ? 'Vencida' : 'Em aberto'}
                                    color={concluida ? 'success' : vencida ? 'error' : 'warning'}
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                />
                            </Stack>
                        );
                    })}
                </Stack>
            )}
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: 1, borderColor: 'divider' }}>
                <Link component="button" type="button" underline="hover" onClick={() => navigate(`/${tenant}/acoes`)}
                    sx={{ fontWeight: 700, color: AZUL_CLARO }}>
                    Ver todas as ações
                </Link>
            </Box>
        </Paper>
    );
}
